import { z } from 'zod';
import { CurrencyCode } from './site.js';
import { OwnPhotoOriginalRef } from './own-photo.js';

// Cart lives entirely in browser localStorage. Server has no carts/cart_items
// tables — checkout snapshots are forwarded to a-manager (system of record).
// Crop preview is rendered with CSS background-position/size; no per-item
// Storage objects, no signed URLs, no pipeline status.

/**
 * CropData — describes the visible window inside the original wallpaper image,
 * expressed as fractional offsets and dimensions (0..1). Same shape we send
 * to a-manager at checkout and feed to <div style="background-..."/>.
 */
export const cropDataSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().min(0).max(1),
  height: z.number().min(0).max(1),
  scaleX: z.number().default(1),
  scaleY: z.number().default(1),
});
export type CropData = z.infer<typeof cropDataSchema>;

/**
 * Line-item source discriminator. `'catalog'` (default) — a picked wallpaper,
 * `wallpaper_id` present, `original` absent. `'own_photo'` — a shopper-uploaded
 * image, `original` present, `wallpaper_id` absent. See the `superRefine` XOR
 * guard below and 2026-07-18-custom-photo-order-page spec §"Cart/order
 * snapshot". Additive discriminator (not a `z.discriminatedUnion`) so existing
 * catalog items keep parsing unchanged — `source` defaults to `'catalog'` when
 * omitted, matching every line ever written before this field existed.
 */
export const CART_ITEM_SOURCES = ['catalog', 'own_photo'] as const;
export const CartItemSource = z.enum(CART_ITEM_SOURCES);
export type CartItemSource = z.infer<typeof CartItemSource>;

/** A single line in the cart, as persisted in localStorage. */
export const CartItem = z
  .object({
    // Stable client-generated id (uuid). Used as React key + de-dup.
    item_id: z.string().uuid(),
    source: CartItemSource.default('catalog'),
    // Required when source==='catalog', absent when source==='own_photo' —
    // enforced by the superRefine below, not by the base object shape (kept
    // optional here so a bare `z.object` stays additive/back-compatible).
    wallpaper_id: z.number().int().positive().optional(),
    // Required when source==='own_photo', absent when source==='catalog'.
    original: OwnPhotoOriginalRef.optional(),
    material_id: z.number().int().positive(),
    width_cm: z.number().int().positive().max(2000),
    height_cm: z.number().int().positive().max(1000),
    crop_data: cropDataSchema,
    roll_count: z.number().int().positive(),
    area_m2: z.number().positive(),
    // Prices stored alongside so reload doesn't recompute. Strings to keep
    // exact decimals across JSON round-trips.
    price_total: z.string(),
    price_discounted: z.string(),
    currency: CurrencyCode,
    added_at: z.string().datetime(),
  })
  .superRefine((item, ctx) => {
    if (item.source === 'catalog') {
      if (item.wallpaper_id === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['wallpaper_id'],
          message: "wallpaper_id is required when source is 'catalog'",
        });
      }
      if (item.original !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['original'],
          message: "original must be absent when source is 'catalog'",
        });
      }
    } else {
      if (item.original === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['original'],
          message: "original is required when source is 'own_photo'",
        });
      }
      if (item.wallpaper_id !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['wallpaper_id'],
          message: "wallpaper_id must be absent when source is 'own_photo'",
        });
      }
    }
  });
export type CartItem = z.infer<typeof CartItem>;

/** Whole cart payload — what we keep in localStorage. */
export const Cart = z.object({
  // First-visit-generated client identifier; used by a-manager for
  // abandoned-cart recovery and analytics. Not tied to auth.
  client_token: z.string().uuid(),
  site_code: z.string(),
  lang_code: z.string(),
  currency: CurrencyCode,
  items: z.array(CartItem),
  updated_at: z.string().datetime(),
});
export type Cart = z.infer<typeof Cart>;

// ---------------------------------------------------------------------------
// Single-currency-per-cart invariant
// ---------------------------------------------------------------------------
//
// The cart carries one currency at the cart level (`Cart.currency`) AND one per
// line (`CartItem.currency`). `OrderPushBody` requires exactly ONE ISO-3
// currency per order, taken from the cart level. In the multi-storefront model
// (three sites, potentially different currencies) a cart could end up with
// lines added under different currencies — silently serialised into one order
// with an ambiguous currency. This guard makes that impossible: the order can
// only be built from a cart whose lines all agree on a single currency, and
// where that currency matches the cart-level field.
//
// NOTE: this is the *uniqueness* invariant only. Deciding WHICH currency a
// storefront uses (a `site_code → currency` authority for order currency) is a
// money / a-manager field-mapping concern, tracked separately and intentionally
// out of scope here.

/**
 * Thrown when a cart violates the single-currency invariant at checkout.
 * `currencies` lists every distinct currency seen (cart-level + per-line), in
 * first-seen order, so callers can render a precise message.
 */
export class MixedCartCurrencyError extends Error {
  /** Stable discriminant for typed handling / i18n keying. */
  public readonly code = 'mixed_cart_currency' as const;
  constructor(
    message: string,
    /** Distinct currencies observed, in first-seen order (≥ 2 when thrown). */
    public readonly currencies: string[],
  ) {
    super(message);
    this.name = 'MixedCartCurrencyError';
  }
}

/**
 * Collect every distinct currency present in a cart — the cart-level currency
 * plus each line's currency — in first-seen order. An empty cart yields just
 * the cart-level currency.
 *
 * Pure: no I/O, no clock, no mutation.
 */
export function cartCurrencies(cart: Pick<Cart, 'currency' | 'items'>): string[] {
  const seen: string[] = [];
  const push = (c: string): void => {
    if (!seen.includes(c)) seen.push(c);
  };
  push(cart.currency);
  for (const item of cart.items) push(item.currency);
  return seen;
}

/**
 * Returns `true` when the cart satisfies the single-currency invariant: the
 * cart-level currency and every line currency are the same ISO-3 code. An empty
 * cart is trivially single-currency. Never throws — use {@link assertSingleCartCurrency}
 * for the throwing variant at the checkout boundary.
 */
export function isSingleCurrencyCart(cart: Pick<Cart, 'currency' | 'items'>): boolean {
  return cartCurrencies(cart).length <= 1;
}

/**
 * Checkout-boundary guard: asserts the cart holds exactly one currency across
 * its cart-level field and all lines. Throws {@link MixedCartCurrencyError}
 * (carrying the distinct currencies) when violated; returns the single currency
 * otherwise. The normal single-site case (one currency everywhere) is the happy
 * path and stays frictionless.
 *
 * Pure: no I/O, no clock, no mutation.
 */
export function assertSingleCartCurrency(cart: Pick<Cart, 'currency' | 'items'>): string {
  const currencies = cartCurrencies(cart);
  if (currencies.length > 1) {
    throw new MixedCartCurrencyError(
      `Cart contains mixed currencies (${currencies.join(', ')}); an order requires a single currency.`,
      currencies,
    );
  }
  // length is always ≥ 1 (cart-level currency is always present).
  return currencies[0]!;
}
